import { create } from "zustand";
import {
  IDefaultEmailTemplate,
  IEmailTemplate,
  INotificationBaseRelatedToShow,
  NotificationType,
  TypeEmailTemplates,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { registerStore } from "store/utils/reset-store.utils";
import { useDefaultEmailTemplates } from "./default-email-templates.store";

type State = {
  customEmailTemplates: Record<string, Array<IEmailTemplate>>;
  emailTemplates: Record<string, Array<IDefaultEmailTemplate | IEmailTemplate>>;
  showId: string | null;
  isLoading: boolean;
  isLoaded: boolean;
};

type Action = {
  fetch: (showId: string, force?: boolean) => Promise<void>;
  autoCatch: (showId: string) => Promise<void>;
  getByShowId: (
    showId: string
  ) => Array<IDefaultEmailTemplate | IEmailTemplate>;
  getCustomByType: (
    showId: string,
    type: TypeEmailTemplates
  ) => IEmailTemplate | null;
};

export const useShowEmailTemplates = create<State & Action>((set, get) => ({
  customEmailTemplates: {},
  emailTemplates: {},
  isLoading: false,
  isLoaded: false,
  showId: null,

  getByShowId: (
    showId: string
  ): Array<IDefaultEmailTemplate | IEmailTemplate> => {
    const { emailTemplates } = get();

    if (!emailTemplates[showId]) {
      return [];
    }

    return emailTemplates[showId];
  },
  getCustomByType: (
    showId: string,
    type: TypeEmailTemplates
  ): IEmailTemplate | null => {
    const { customEmailTemplates } = get();

    if (!customEmailTemplates[showId]) {
      return null;
    }

    return (
      customEmailTemplates[showId].find((template) => template.type === type) ??
      null
    );
  },

  autoCatch: async (showId: string) => {
    const { fetch } = get();

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.EMAIL_TEMPLATES,
      showId,
      callback: async ({ showId }) => {
        await fetch(showId, true);
      },
    });
  },

  fetch: async (showId: string, force = false) => {
    const {
      isLoaded,
      showId: prevShowId,
      emailTemplates,
      customEmailTemplates,
    } = get();

    if (!force) {
      if (prevShowId === showId && isLoaded) {
        return;
      }
    }

    set(() => ({ isLoading: true }));

    try {
      const {
        fetch: fetchDefaultEmailTemplates,
      } = useDefaultEmailTemplates.getState();

      const defaultEmailTemplates = await fetchDefaultEmailTemplates();

      const emailTemplatesFromShow = await apiV2.emailTemplate.getAll({
        showId,
      });

      const missingEmailTemplates = defaultEmailTemplates.filter(
        (defaultTemplate) =>
          !emailTemplatesFromShow.find(
            (template) => template.type === defaultTemplate.type
          )
      );

      set(() => ({
        isLoaded: true,
        emailTemplates: {
          ...emailTemplates,

          [showId]: [...emailTemplatesFromShow, ...missingEmailTemplates],
        },
        customEmailTemplates: {
          ...customEmailTemplates,

          [showId]: [...emailTemplatesFromShow],
        },
      }));
    } finally {
      set(() => ({ isLoading: false, showId }));
    }
  },
}));

registerStore(useShowEmailTemplates);
